/**
 * What a viewer did on a shared catalogue, batched for one POST. Pure, so the timing rules are
 * testable without a browser: heartbeats carry real elapsed seconds, capped like the server caps.
 */

export type ViewerEventType = "PAGE_VIEW" | "PRODUCT_VIEW" | "HEARTBEAT";

export interface ViewerEvent {
  type: ViewerEventType;
  productUniqueId?: string;
  seconds?: number;
}

/** How often the page notes time spent while visible. */
export const HEARTBEAT_INTERVAL_MS = 15_000;
/** How often queued events are sent. */
export const FLUSH_INTERVAL_MS = 30_000;
/** One heartbeat never claims more than this; longer gaps mean the tab was asleep. */
export const MAX_HEARTBEAT_SECONDS = 60;
export const MAX_QUEUED_EVENTS = 50;

/** Whole seconds between two ticks, clamped to the heartbeat cap; 0 when under a second. */
export function heartbeatSeconds(lastTickMs: number, nowMs: number): number {
  const elapsed = Math.floor((nowMs - lastTickMs) / 1000);
  if (!Number.isFinite(elapsed) || elapsed < 1) return 0;
  return Math.min(MAX_HEARTBEAT_SECONDS, elapsed);
}

export interface EventBuffer {
  push(event: ViewerEvent): void;
  /** Everything queued, newest last; the buffer is empty afterwards. */
  drain(): ViewerEvent[];
  size(): number;
}

export function createEventBuffer(limit = MAX_QUEUED_EVENTS): EventBuffer {
  let items: ViewerEvent[] = [];
  return {
    push(event) {
      if (items.length >= limit) {
        // Keep the newest; the oldest heartbeat is the least useful one to lose.
        items = items.slice(items.length - limit + 1);
      }
      items.push(event);
    },
    drain() {
      const out = items;
      items = [];
      return out;
    },
    size() {
      return items.length;
    },
  };
}

/** One "opened" row per session and page: the key the hook dedupes on. */
export function openEventKey(sessionToken: string, productUniqueId?: string): string {
  return `${sessionToken}|${productUniqueId || ""}`;
}
